<template>
  <!--UPDATE PASSWORD MODAL-->
  <div>
    <md-dialog
      :md-active.sync="showChangePassword"
      :class="{ 'admin-change-password-dialog': isAdmin }"
      :md-click-outside-to-close="false"
      :md-close-on-esc="false"
      class="force-fullscreen change-password-dialog"
    >

      <!-- only rendered for admins -->
      <md-dialog-content v-if="isAdmin">
        <div class="change-password-layout">

          <div class="change-password-left">
            <div class="change-password-heading">
              <h3 class="title">{{ title }}</h3>
              <p class="subtitle">{{ subtitle }}</p>
            </div>

            <img src="/img/icons/password-lock.svg" alt="Password Lock" class="password-lock-img" />
          </div>

          <div class="change-password-right">
            <el-form label-position="top" @submit.native.prevent="handleAdminChangePassword">
              <PasswordFieldsGroup
                ref="passwordFields"
                :password.sync="password"
                :confirm-password.sync="confirmPassword"
                @validity-change="onPasswordValidityChange"
              />

              <el-button
                type="success"
                native-type="submit"
                class="change-password-btn"
                :disabled="isChangeDisabled"
              >
                Change Password
              </el-button>
            </el-form>
          </div>

        </div>
      </md-dialog-content>

      <!-- for non-admin users -->
      <template v-else>
        <md-dialog-title>Change Password</md-dialog-title>

        <md-dialog-content>
          <div class="md-layout">
            <div class="md-layout-item md-size-100 md-small-size-100">
              <strong>You need to change your password to continue.</strong>

              <div style="display: inline-flex; width: 100%">
                <md-field>
                  <label>Password</label>
                  <md-input v-model.trim="password" type="password"></md-input>
                </md-field>
              </div>

              <div style="display: inline-flex; width: 100%">
                <md-field>
                  <label>Confirm Password</label>
                  <md-input v-model.trim="confirmPassword" type="password"></md-input>
                </md-field>
              </div>
            </div>
          </div>
        </md-dialog-content>

        <md-dialog-actions>
          <md-button class="md-success" @click="handleUserChangePassword">Change Password</md-button>
        </md-dialog-actions>
      </template>

    </md-dialog>
  </div>
</template>

<script>
import { loginUrl, isPayer, passwordValidation, isAdmin } from "@/lib/utils";
import constants from "@/constants";
import * as authTypes from "@/store/auth/auth.types";
import { PasswordFieldsGroup } from "@/components";
import { Form, Button } from "element-ui";

const REASON_COPY = {
  expiry: {
    title: "Update Password",
    subtitle: days =>
      `Your password has reached its ${days}-day limit and must be updated before you can continue.`
  },
  forceReset: {
    title: "Change Password",
    subtitle: () =>
      "An Admin has requested you to change your password. Change your password to continue."
  }
};

export default {
  components: {
    PasswordFieldsGroup,
    ElForm: Form,
    ElButton: Button
  },

  data() {
    return {
      confirmPassword: "",
      password: "",
      showChangePassword: false,

      title: REASON_COPY.forceReset.title,
      subtitle: REASON_COPY.forceReset.subtitle(),

      isPasswordFieldsValid: false,
    };
  },

  computed: {
    isAdmin() {
      return isAdmin();
    },

    isChangeDisabled() {
      return !this.password || !this.confirmPassword || !this.isPasswordFieldsValid;
    }
  },

  methods: {
    onPasswordValidityChange({ isValid }) {
      this.isPasswordFieldsValid = isValid;
    },

    handleAdminChangePassword() {
      this.$refs.passwordFields.triggerMismatchCheck();

      if (!this.isPasswordFieldsValid) {
        return;
      }

      this.submitPasswordChange();
    },

    handleUserChangePassword() {
      const { confirmPassword, password } = this;

      const isValidPassword = passwordValidation(password, this.notification);
      if (password !== confirmPassword) {
        this.notification("Passwords do not match", "pan_tool", "danger");
      }

      const isValidForm = isValidPassword && (password == confirmPassword);
      if (!isValidForm) {
        return;
      }

      this.submitPasswordChange();
    },

    submitPasswordChange() {
      const { confirmPassword, password } = this;
      const userID = this.$store.state.auth.userData.user.id;
      const redirectUrl = loginUrl();
      const roleType = isPayer() ? 'payers' : 'admins';
      this.$http
        .post(`auth/local/${roleType}/${userID}/password`, {
          method: "PUT",
          body: {
            password
          }
        })
        .then((response) => {
          const responseData = response.data;
          if (responseData.error) {
            this.notification(
              responseData.message || "Something went wrong. Please try again.",
              "pan_tool",
              "danger"
            );
            return;
          }

          this.notification(
            "Password changed successfully. Kindly login again.",
            "done",
            "success"
          );

          this.$store.dispatch(authTypes.LOGIN_USER, {});
          this.$store.dispatch(authTypes.PARENT_LOGIN_USER, false);
          localStorage.removeItem('etax');
          sessionStorage.removeItem('user');

          if (this.$router.currentRoute.name === "Payer Login") {
            setTimeout(() => {
              window.location.href = redirectUrl
            }, 2000);
          } else {
            this.$router.push(redirectUrl);
          }
        })
        .catch(() => {
          this.notification(
            "Something went wrong. Please try again.",
            "pan_tool",
            "danger"
          );
        });
    },

    notification(message, icon, color) {
      this.$notify({
        message: message,
        icon: icon,
        horizontalAlign: "right",
        verticalAlign: "top",
        type: color
      });
    }
  },

  mounted() {
    const authData = this.$store.state.auth.userData;
    const extraDetails = authData.auth_data && authData.auth_data.extra_details;
    const forcePasswordReset = extraDetails && extraDetails.force_password_reset;
    const isPasswordExpired = extraDetails && extraDetails.is_password_expired;
    const passwordExpiryDays = extraDetails && extraDetails.password_expiry_days;

    const canChangePwd = isPayer() ? constants.userCan(['auth.updatepassword']) : forcePasswordReset;
    this.showChangePassword = canChangePwd;

    if (isPasswordExpired) {
      this.title = REASON_COPY.expiry.title;
      this.subtitle = REASON_COPY.expiry.subtitle(passwordExpiryDays);
    } else if (forcePasswordReset) {
      this.title = REASON_COPY.forceReset.title;
      this.subtitle = REASON_COPY.forceReset.subtitle();
    }
  }
};
</script>

<style scoped>
.change-password-dialog {
  width: 830px !important;
}

.change-password-dialog.admin-change-password-dialog {
  width: 836px !important;
  border-radius: 16px !important;
}

.change-password-layout {
  display: flex;
  gap: 60px;
  flex-wrap: wrap;
  padding: 16px;
}

.change-password-left {
  flex: 1;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
}

.change-password-heading {
  margin-bottom: 32px;
}

.change-password-left .title {
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 8px;
  color: #1D2739;
}

.change-password-left .subtitle {
  color: #475367;
  margin: 0;
}

.password-lock-img {
  max-width: 160px;
  width: 100%;
  height: auto;
  align-self: center;
  animation: lock-wiggle 3s ease-in-out infinite;
  margin: 40px 0 auto;
}

@keyframes lock-wiggle {
  0%, 100% { transform: rotate(0deg); }
  10% { transform: rotate(-6deg); }
  20% { transform: rotate(6deg); }
  30% { transform: rotate(-4deg); }
  40% { transform: rotate(4deg); }
  50%, 100% { transform: rotate(0deg); }
}

.change-password-right {
  flex: 1;
  min-width: 320px;
}

.change-password-btn {
  width: 100%;
  height: 44px;
  background-color: #50AE55;
}

.change-password-btn.is-disabled {
  background-color: #D0D5DD;
  border-color: #D0D5DD;
  color: #ffffff;
}
</style>